// @flow
import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
// import {useHistory} from 'react-router-dom';
import {makeStyles} from '@material-ui/styles';
import {Grid, Typography} from '@material-ui/core';
import {CheckCircle, RadioButtonUnchecked} from '@material-ui/icons';

import {usePage} from '../../../page/utils';
import {colors} from '../../../../assets/styles/colors';

import HeaderTitle from '../components/HeaderTitle/HeaderTitle';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import firstTemplate from '../../../../assets/img/legal/template-1.svg';
import secondTemplate from '../../../../assets/img/legal/template-2.svg';
import thirdTemplate from '../../../../assets/img/legal/template-3.svg';
import SelectLayout from '../components/Layouts/SelectLayouts';
import LoadingSpinner from '../../../../elements/LoadingSpinner/LoadingSpinner';
import SnackBar from '../../../../elements/SnackBar/SnackBar';
import {useIsFeatureInTier} from '../../../../utils/component';

import type {SaveLegalRequest} from '../api';
import type {AboutUs as AboutUsType} from '../duck';
import {useTypedSelector} from '../../../../store';
type Props = {
  saveStoreAboutUsConfig: (saveLegalRequest: SaveLegalRequest) => void,
  fetchStoreAboutUsConfig: () => void,
  isFetchingAboutUs?: boolean,
  saveAboutUsError?: string,
  saveAboutUsSuccess?: boolean,
  isSaving?: boolean,
  aboutUs?: AboutUsType,
  resetState: () => void,
};

const useStyles = makeStyles(theme => ({
  root: {
    background: '#FFFFFF',
    padding: theme.spacing(2, 3),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2),
    },
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    borderRadius: '12px',
    overflow: 'hidden',
  },
  section: {
    margin: theme.spacing(1, 0),
  },
  title: {
    fontWeight: 600,
    fontSize: 14,
    color: colors.grey900,
  },
  gridTemplates: {
    margin: theme.spacing(2, 0),
  },
  cardButton: {
    width: '100%',
    height: '100%',
    padding: 0,
    borderRadius: '8px',
    overflow: 'hidden',
    '& span': {
      height: '100%',
    },
  },
  greyCard: {
    position: 'relative',
    background: colors.grey200,
    // borderRadius: '8px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(2, 0),
    height: '100%',
    width: '100%',
  },
  imgTemplate: {
    width: 'fit-content',
    borderRadius: '6px',
    background: '#FFFFFF',
    padding: theme.spacing(1),
  },
  floatCheckbox: {
    position: 'absolute',
    left: 16,
    top: 16,
  },
}));

const TEMPLATE_TYPE = {
  LARGE: 0,
  MEDIUM: 1,
  SMALL: 2,
};
function AboutUs(props: Props) {
  const {
    aboutUs,
    isSaving,
    saveAboutUsError,
    saveAboutUsSuccess,
    isFetchingAboutUs,
    fetchStoreAboutUsConfig,
    saveStoreAboutUsConfig,
  } = props;

  const classes = useStyles();
  const legalSetting = useTypedSelector(
    state => state.legalConfig.legalSetting
  );
  const [layoutSelected, setLayoutSelected] = useState(0);

  const hasAboutUsInformationFeature = useIsFeatureInTier('STORE_POLICIES');

  const checkedCheckbox = (
    <CheckCircle color="primary" style={{fontSize: '1.75rem'}} />
  );
  const uncheckedCheckbox = (
    <RadioButtonUnchecked style={{fontSize: '1.75rem'}} />
  );

  useEffect(() => {
    if (aboutUs && aboutUs.storeAboutInformationType) {
      const key = aboutUs.storeAboutInformationType;
      if (TEMPLATE_TYPE[key]) {
        setLayoutSelected(TEMPLATE_TYPE[key]);
      }
    }
  }, [aboutUs]);

  useEffect(() => {
    if (hasAboutUsInformationFeature) {
      fetchStoreAboutUsConfig();
    }
  }, [fetchStoreAboutUsConfig, hasAboutUsInformationFeature]);

  // useEffect(() => {
  //   if (saveAboutUsSuccess) history.push('/my-store/pages');
  // }, [history, saveAboutUsSuccess]);

  return (
    <div className={classes.root}>
      {(isFetchingAboutUs || isSaving) && <LoadingSpinner />}
      <SnackBar
        mode="success"
        message={saveAboutUsSuccess ? 'Configuración guardada' : ''}
      />
      <SnackBar
        mode="error"
        message={!!saveAboutUsError ? saveAboutUsError : ''}
      />
      <HeaderTitle
        title="Acerca de nosotros"
        description="Proporcionar al cliente más información sobre su negocio y producto."
        enabledKeyName="aboutInformationEnabled"
        enabled={legalSetting?.aboutInformationEnabled}
      />
      <section className={classes.section}>
        <Typography gutterBottom className={classes.title}>
          1. Elige la plantilla de tu preferencia:
        </Typography>
        <Grid container spacing={2} className={classes.gridTemplates}>
          <Grid item xs={12} md={4}>
            <CustomButton
              disabled={!hasAboutUsInformationFeature}
              className={classes.cardButton}
              onClick={() => setLayoutSelected(0)}
            >
              <div className={classes.greyCard}>
                <div className={classes.floatCheckbox}>
                  {layoutSelected === 0 ? checkedCheckbox : uncheckedCheckbox}
                </div>
                <div className={classes.imgTemplate}>
                  <img src={firstTemplate} alt="first template" />
                </div>
              </div>
            </CustomButton>
          </Grid>
          <Grid item xs={12} md={4}>
            <CustomButton
              disabled={!hasAboutUsInformationFeature}
              className={classes.cardButton}
              onClick={() => setLayoutSelected(1)}
            >
              <div className={classes.greyCard}>
                <div className={classes.floatCheckbox}>
                  {layoutSelected === 1 ? checkedCheckbox : uncheckedCheckbox}
                </div>
                <div className={classes.imgTemplate}>
                  <img src={secondTemplate} alt="second template" />
                </div>
              </div>
            </CustomButton>
          </Grid>
          <Grid item xs={12} md={4}>
            <CustomButton
              disabled={!hasAboutUsInformationFeature}
              className={classes.cardButton}
              onClick={() => setLayoutSelected(2)}
            >
              <div className={classes.greyCard}>
                <div className={classes.floatCheckbox}>
                  {layoutSelected === 2 ? checkedCheckbox : uncheckedCheckbox}
                </div>
                <div className={classes.imgTemplate}>
                  <img src={thirdTemplate} alt="third template" />
                </div>
              </div>
            </CustomButton>
          </Grid>
        </Grid>
      </section>
      <section className={classes.section}>
        <Typography gutterBottom className={classes.title}>
          2. Elige la plantilla de tu preferencia:
        </Typography>
        <SelectLayout
          aboutUs={aboutUs}
          layoutSelected={layoutSelected}
          saveStoreAboutUsConfig={saveStoreAboutUsConfig}
        />
      </section>
    </div>
  );
}

AboutUs.propTypes = {
  saveStoreAboutUsConfig: PropTypes.func.isRequired,
  fetchStoreAboutUsConfig: PropTypes.func,
  isFetchingAboutUs: PropTypes.bool,
  saveAboutUsError: PropTypes.string,
  saveAboutUsSuccess: PropTypes.bool,
  isSaving: PropTypes.bool,
  aboutUs: PropTypes.object,
};
export default usePage<Props>(AboutUs, 'About us configuration');
